import { useState, useMemo, useRef, type FormEvent, type KeyboardEvent } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { Plus, ChevronRight, ArrowUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PriorityTile } from './PriorityTile'
import { usePrioritiesByBucket, useCreatePriority, useReorderPriority } from '@/hooks/usePriorities'
import { cn } from '@/lib/utils'
import type { Priority } from '@/types'

const ACTIVE_STATUSES = new Set<Priority['status']>(['aspirational', 'active', 'completed', 'released'])

interface PriorityListProps {
  bucketId: string
  bucketColor: string
  bucketIcon?: string
  onSelectPriority: (id: string) => void
  insights?: Record<string, string>
  bucketMisaligned?: boolean
}

const ORIGIN_ORDER: Record<string, number> = { chosen: 0, obligation: 1 }

export function PriorityList({ bucketId, bucketColor, bucketIcon, onSelectPriority, insights, bucketMisaligned }: PriorityListProps) {
  const priorities = usePrioritiesByBucket(bucketId)
  const [dormantExpanded, setDormantExpanded] = useState(false)
  const [sortByOrigin, setSortByOrigin] = useState(false)
  const createPriority = useCreatePriority()
  const reorderPriority = useReorderPriority()

  const activePriorities = useMemo(
    () => priorities?.filter((p) => ACTIVE_STATUSES.has(p.status)) ?? [],
    [priorities],
  )

  const hasOriginData = activePriorities.some((p) => p.origin != null)

  const displayedPriorities = useMemo(() => {
    if (!sortByOrigin) return activePriorities
    return [...activePriorities].sort((a, b) => {
      const ao = a.origin ? (ORIGIN_ORDER[a.origin] ?? 0) : 0
      const bo = b.origin ? (ORIGIN_ORDER[b.origin] ?? 0) : 0
      if (ao !== bo) return ao - bo
      return a.rank - b.rank
    })
  }, [activePriorities, sortByOrigin])
  const dormantPriorities = useMemo(
    () => priorities?.filter((p) => p.status === 'dormant') ?? [],
    [priorities],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = activePriorities.findIndex((p) => p.id === active.id)
    const newIndex = activePriorities.findIndex((p) => p.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(activePriorities, oldIndex, newIndex)
    const moved = reordered[newIndex] as Priority
    const beforeRank = newIndex > 0 ? (reordered[newIndex - 1] as Priority).rank : null
    const afterRank = newIndex < reordered.length - 1 ? (reordered[newIndex + 1] as Priority).rank : null

    reorderPriority.mutate({ id: moved.id, bucketId, beforeRank, afterRank })
  }

  // Quick-create inline input
  const [isAdding, setIsAdding] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleCreate(e?: FormEvent) {
    e?.preventDefault()
    const title = newTitle.trim()
    if (!title) { setIsAdding(false); return }
    await createPriority.mutateAsync({ bucketId, title })
    setNewTitle('')
    inputRef.current?.focus()
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setNewTitle('')
      setIsAdding(false)
    }
  }

  const isEmpty = priorities !== undefined
    && activePriorities.length === 0
    && dormantPriorities.length === 0

  return (
    <div className="pl-1">
      {/* Origin sort toggle — only shown when at least one priority has origin set */}
      {hasOriginData && (
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={() => setSortByOrigin((s) => !s)}
            className={cn(
              'flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors',
              sortByOrigin
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
            )}
            title={sortByOrigin ? 'Back to rank order' : 'Sort by origin (chosen first)'}
            aria-pressed={sortByOrigin}
          >
            <ArrowUpDown size={9} />
            {sortByOrigin ? 'origin' : 'rank'}
          </button>
        </div>
      )}

      {/* Active priorities — DnD sortable (disabled when sorting by origin) */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={sortByOrigin ? undefined : handleDragEnd}
      >
        <SortableContext
          items={displayedPriorities.map((p) => p.id)}
          strategy={verticalListSortingStrategy}
        >
          {displayedPriorities.map((p) => (
            <PriorityTile
              key={p.id}
              priority={p}
              bucketColor={bucketColor}
              bucketIcon={bucketIcon}
              onOpenDetail={() => onSelectPriority(p.id)}
              coachingTeaser={insights?.[p.id]}
              bucketMisaligned={bucketMisaligned}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Empty state */}
      {isEmpty && !isAdding && (
        <p className="text-xs text-muted-foreground italic py-1">No priorities yet.</p>
      )}

      {/* Quick-create */}
      {isAdding ? (
        <form onSubmit={handleCreate} className="-ml-1 mt-2">
          <Input
            ref={inputRef}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onBlur={() => void handleCreate()}
            onKeyDown={handleInputKeyDown}
            placeholder="Priority name…"
            autoFocus
            className="h-11 md:h-10 text-sm"
            aria-label="New priority name"
          />
        </form>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsAdding(true)}
          className="-ml-1 mt-2 h-11 md:h-9 gap-1.5 px-3.5"
        >
          <Plus />
          Add priority
        </Button>
      )}

      {/* Dormant section — collapsible */}
      {dormantPriorities.length > 0 && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setDormantExpanded((prev) => !prev)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
            aria-expanded={dormantExpanded}
          >
            <ChevronRight
              size={12}
              className={cn('transition-transform duration-150', dormantExpanded && 'rotate-90')}
            />
            {dormantPriorities.length} dormant
          </button>

          {dormantExpanded && (
            <div className="opacity-60">
              {dormantPriorities.map((p) => (
                <PriorityTile
                  key={p.id}
                  priority={p}
                  bucketColor={bucketColor}
                  onOpenDetail={() => onSelectPriority(p.id)}
                  draggingDisabled
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
